/**
 * Carga estudio desde manifest JSON (R2/S3).
 * Instancias:
 *  - { url: "https://..." } (presigner / absolutas)
 *  - { path: "tjsiddse/.../file.dcm" } (CDN; base = origin del manifestUrl)
 *  - { key: "..." } path relativo (flat UUID.dcm)
 */
import throttle from 'lodash/throttle'
import { groupBy } from '../functions'
import { configureWadoLoaderAuth } from './wadoAuth'
import {
  listCompleteSopsForStudy,
  getCombinedStudyCacheStats,
} from '../db/dicomCache'
import { syncStudyCatalogFromManifest } from '../db/studyCatalog'
import { getWriteQueueStats } from '../db/dicomCacheWriteQueue'
import {
  loadDicomInstanceDirectUrl,
  sopFromManifestInstance,
  fallbackCacheKey,
} from './loadDicomInstanceWithCache'
import { loadInstanceForMode } from './loadInstanceForMode'
import { RENDER_MODE } from './renderMode'
import {
  isStudyLoadCancelled,
  throwIfStudyLoadCancelled,
  createStudyLoadAbortError,
} from './studyLoadSource'
import { uniquifySeriesNumbers } from './uniquifySeriesNumbers'
import {
  resolveLoadUrl,
  resolveDiagnosticUrl,
  resolvePreviewUrl,
  inspectManifestMedia,
} from './renderModeUrls'
import {
  canonicalizeManifest,
  DICOM_FETCH_TYPE,
  isReportLooseSeriesInst,
} from './manifestNormalize'
import { copyGeometryFromManifest, stampSliceGeometry } from './seriesLink'
import { spreadStackGeometryFromFiles } from './previewSliceGeometry'
import {
  getManifestDownloadConcurrency,
  getManifestReduxFlushMs,
  getConcurrencyHardCap,
  describeDownloadProfile,
  isConstrainedUiDevice,
  isPhoneLikeDevice,
} from './mobileDownloadProfile'
import { isScoutSeries, stampIsScoutOnFileItem } from './isScoutSeries'
import {
  isNonImageSeriesInst,
  isPdfDocumentSeriesInst,
  stampNonImageOnFileItem,
} from './isNonImageInstance'
import { stampIsRefOnFileItem } from './isRefSeries'
import {
  buildSeriesSlots,
  createSeriesPriorityScheduler,
  createDownloadProgressTracker,
  estimateLoadedBytes,
} from './downloadSeriesScheduler'
import {
  getR2ZipRangeMode,
  getCoalescingFetcherCount,
  FIRST_BLOCK_MAX_BYTES,
  MAX_COALESCED_BLOCK_SIZE_BYTES,
  MAX_COALESCED_MEMBERS,
  R2_ZIP_RANGE_MODE,
} from './r2ZipDownloadPrefs'
import { buildCoalescedBlock, slicePart10FromBlock } from './zipRangeCoalesce'
import {
  fetchZipRangeBlock,
  decodeAndCachePart10Buffer,
} from './loadDicomFromZipRange'
import {
  getAdjustedConcurrency,
  shouldDeferHeavyUiFlush,
  subscribeInteraction,
  scheduleWhenIdle,
} from './viewportInteractionDetector'

function windowFromManifestInst(inst) {
  if (!inst || typeof inst !== 'object') {
    return { windowWidth: null, windowCenter: null }
  }
  const windowWidth =
    inst.windowWidth != null && Number.isFinite(Number(inst.windowWidth))
      ? Number(inst.windowWidth)
      : null
  const windowCenter =
    inst.windowCenter != null && Number.isFinite(Number(inst.windowCenter))
      ? Number(inst.windowCenter)
      : null
  return { windowWidth, windowCenter }
}

/**
 * @param {{ username?: string, password?: string } | null} [credentials]
 */
export function configureWadoLoaderForManifestAndOrthanc(credentials) {
  configureWadoLoaderAuth(credentials || null)
}

/**
 * Metadata de fetch DICOM para upgrade on-demand (PREVIEW → DCM).
 * Reports loose (Path M): nunca RANGE_ZIP; offsets/archiveUrl se ignoran.
 * @param {object|null|undefined} inst
 * @param {object|null|undefined} [series]
 * @returns {{
 *   archiveUrl: string|null,
 *   byteOffset: number|null,
 *   byteLength: number|null,
 *   dicomFetchType: string|null,
 * }}
 */
export function extractDicomFetchMeta(inst, series) {
  if (!inst || typeof inst !== 'object') {
    return {
      archiveUrl: null,
      byteOffset: null,
      byteLength: null,
      dicomFetchType: null,
    }
  }
  if (
    isReportLooseSeriesInst(series || null, inst) ||
    inst.dicomFetchType === DICOM_FETCH_TYPE.DIRECT_URL
  ) {
    return {
      archiveUrl: null,
      byteOffset: null,
      byteLength: null,
      dicomFetchType: DICOM_FETCH_TYPE.DIRECT_URL,
    }
  }
  const byteOffset =
    inst.byteOffset != null && Number.isFinite(Number(inst.byteOffset))
      ? Number(inst.byteOffset)
      : null
  const byteLength =
    inst.byteLength != null && Number.isFinite(Number(inst.byteLength))
      ? Number(inst.byteLength)
      : null
  const archiveUrl =
    inst.archiveUrl != null && String(inst.archiveUrl).trim()
      ? String(inst.archiveUrl).trim()
      : null
  let dicomFetchType =
    inst.dicomFetchType != null ? String(inst.dicomFetchType) : null
  if (
    !dicomFetchType &&
    archiveUrl &&
    byteOffset != null &&
    byteOffset >= 0 &&
    byteLength != null &&
    byteLength > 0
  ) {
    dicomFetchType = DICOM_FETCH_TYPE.RANGE_ZIP
  }
  return {
    archiveUrl,
    byteOffset,
    byteLength,
    dicomFetchType,
  }
}

function buildExplorerData(items) {
  const patientList = groupBy(items, (a) => a.patient.patientName)
  return {
    folder: null,
    patient: {
      list: patientList,
      keys: [...patientList.keys()],
    },
  }
}

function validateManifest(m) {
  if (!m || typeof m !== 'object') return 'Manifest inválido'
  if (!Array.isArray(m.series) || m.series.length === 0) return 'El manifest no contiene series'
  return null
}

function formatStudyDateTime(studyDate, studyTime) {
  const d = studyDate != null ? String(studyDate).trim() : ''
  const t = studyTime != null ? String(studyTime).trim() : ''
  if (d && t) return `${d}.${t}`
  return d || t || ''
}

/** Origin + '/' desde URL del manifest (CDN público). */
export function originBaseFromUrl(url) {
  try {
    const u = new URL(url)
    return `${u.origin}/`
  } catch (e) {
    return null
  }
}

/** Encode por segmento (espacios/tildes) conservando '/'. */
export function encodeObjectStoragePath(relPath) {
  const cleaned = String(relPath || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
  return cleaned
    .split('/')
    .filter((seg) => seg.length > 0)
    .map((seg) => {
      try {
        return encodeURIComponent(decodeURIComponent(seg))
      } catch (e) {
        return encodeURIComponent(seg)
      }
    })
    .join('/')
}

/**
 * Resuelve URL de descarga.
 * Prioridad: url http(s) → path / key / url relativa + assetBaseUrl.
 */
export function resolveInstanceAssetUrl(inst, assetBaseUrl) {
  if (!inst || typeof inst !== 'object') return null

  const rawUrl = inst.url != null ? String(inst.url).trim() : ''
  if (/^https?:\/\//i.test(rawUrl)) {
    return rawUrl
  }

  const rel =
    (inst.path != null && String(inst.path).trim()) ||
    (inst.key != null && String(inst.key).trim()) ||
    (rawUrl && !/^https?:\/\//i.test(rawUrl) ? rawUrl : '') ||
    ''

  if (!rel) return null
  if (/^https?:\/\//i.test(rel)) return rel

  if (!assetBaseUrl) return null
  const baseNorm = String(assetBaseUrl).endsWith('/')
    ? String(assetBaseUrl)
    : `${assetBaseUrl}/`
  return `${baseNorm}${encodeObjectStoragePath(rel)}`
}

function resolveAssetBaseUrl(manifest, handlers) {
  if (handlers && handlers.assetBaseUrl) {
    const s = String(handlers.assetBaseUrl)
    return s.endsWith('/') ? s : `${s}/`
  }
  const fromManifest =
    (manifest &&
      (manifest.assetBaseUrl || manifest.baseUrl || manifest.cdnBase || manifest.cdnUrl)) ||
    null
  if (fromManifest) {
    const s = String(fromManifest)
    return s.endsWith('/') ? s : `${s}/`
  }
  if (handlers && handlers.manifestUrl) {
    return originBaseFromUrl(handlers.manifestUrl)
  }
  return null
}

function getAutoManifestParallelLimit(taskCount) {
  return getManifestDownloadConcurrency(taskCount)
}

function resolveConcurrency(requested, taskCount) {
  if (taskCount < 1) return 1
  if (requested == null) {
    return getManifestDownloadConcurrency(taskCount)
  }
  if (requested === 0 || requested === Infinity) {
    return getAutoManifestParallelLimit(taskCount)
  }
  if (!Number.isFinite(requested)) {
    return getAutoManifestParallelLimit(taskCount)
  }
  // Techo de seguridad según recursos (no fijos phone=6 / tablet=8)
  const hardCap = getConcurrencyHardCap()
  return Math.max(1, Math.min(Math.floor(requested), taskCount, hardCap))
}

export async function loadStudyFromManifestObject(manifestInput, handlers) {
  const {
    setFilesStore,
    onExplorerUpdate,
    isOpenStore,
    activeDcmIndex,
    concurrency,
    onDownloadProgress,
    getActiveSeriesIdx,
    getPinnedSeriesIdx,
    getScrollFocus,
    onInstanceReady,
    orthancCredentials,
    getRenderMode,
    signal,
    isCancelled,
    skipIdbWrite: skipIdbWriteFromHandlers,
    skipIdbRead: skipIdbReadFromHandlers,
    offlineCacheAllowed,
  } = handlers || {}
  const skipIdbWrite = !!skipIdbWriteFromHandlers
  const skipIdbRead = !!skipIdbReadFromHandlers

  const notifyInstanceReady = (item, meta) => {
    if (typeof onInstanceReady !== 'function' || !item) return
    try {
      onInstanceReady(item, meta || null)
    } catch (e) {
      console.warn('loadStudyFromManifest: onInstanceReady', e)
    }
  }

  const cancelled = () => isStudyLoadCancelled(signal, isCancelled)
  const assertNotCancelled = () => throwIfStudyLoadCancelled(signal, isCancelled)

  const resolveActiveSeriesIdx = () => {
    if (typeof getActiveSeriesIdx === 'function') {
      try {
        const v = getActiveSeriesIdx()
        const n = typeof v === 'number' && Number.isFinite(v) ? Math.floor(v) : 0
        return n
      } catch (e) {
        return 0
      }
    }
    return 0
  }

  const resolveScrollFocus = () => {
    if (typeof getScrollFocus !== 'function') {
      return {
        seriesIdx: resolveActiveSeriesIdx(),
        sliceIndex: 0,
        radius: 2,
      }
    }
    try {
      const raw = getScrollFocus()
      if (!raw || typeof raw !== 'object') {
        return {
          seriesIdx: resolveActiveSeriesIdx(),
          sliceIndex: 0,
          radius: 2,
        }
      }
      return {
        seriesIdx:
          typeof raw.seriesIdx === 'number' && Number.isFinite(raw.seriesIdx)
            ? Math.floor(raw.seriesIdx)
            : resolveActiveSeriesIdx(),
        sliceIndex: Math.max(0, Math.floor(Number(raw.sliceIndex) || 0)),
        radius:
          raw.radius != null
            ? Math.max(0, Math.min(8, Math.floor(Number(raw.radius) || 2)))
            : 2,
      }
    } catch (e) {
      return {
        seriesIdx: resolveActiveSeriesIdx(),
        sliceIndex: 0,
        radius: 2,
      }
    }
  }

  const resolvePinnedSeriesIdx = () => {
    if (typeof getPinnedSeriesIdx !== 'function') return []
    try {
      const raw = getPinnedSeriesIdx()
      if (!Array.isArray(raw)) return []
      return raw.filter((v) => typeof v === 'number' && Number.isFinite(v))
    } catch (e) {
      return []
    }
  }

  const resolveMode = () => {
    // Preferir mode fijo del handler (reload/toggle)
    if (handlers && isValidMode(handlers.renderMode)) {
      return handlers.renderMode
    }
    if (typeof getRenderMode === 'function') {
      try {
        const m = getRenderMode()
        if (isValidMode(m)) return m
      } catch (e) {
        /* ignore */
      }
    }
    return RENDER_MODE.DIAGNOSTIC
  }

  function isValidMode(m) {
    return m === RENDER_MODE.PREVIEW || m === RENDER_MODE.DIAGNOSTIC
  }

  configureWadoLoaderForManifestAndOrthanc(orthancCredentials)

  // Resolver base antes de canonicalize (keys webp/downloadKey → https)
  const assetBaseUrl = resolveAssetBaseUrl(manifestInput, handlers || {})
  const archiveUrlOverride =
    handlers && handlers.archiveUrlOverride != null
      ? String(handlers.archiveUrlOverride).trim() || null
      : handlers && handlers.downloadUrl != null
        ? String(handlers.downloadUrl).trim() || null
        : null
  const manifest = canonicalizeManifest(manifestInput, {
    assetBaseUrl,
    archiveUrlOverride,
  })
  const err = validateManifest(manifest)
  if (err) throw new Error(err)

  const initialMode = resolveMode()

  const studyUid = manifest.studyInstanceUID || ''
  const studyDate = manifest.studyDate || ''
  const studyTime = manifest.studyTime || ''
  const patientName = manifest.patientName || 'Unknown Patient'
  const patientId = manifest.patientId || 'N/A'
  const studyDescription = manifest.studyDescription || ''

  const seriesSorted = [...manifest.series].sort(
    (a, b) => parseInt(a.seriesNumber, 10) - parseInt(b.seriesNumber, 10)
  )

  const tasks = []
  let seriesIdx = 0
  for (const series of seriesSorted) {
    const instances = [...(series.instances || [])].sort(
      (a, b) => parseInt(a.instanceNumber, 10) - parseInt(b.instanceNumber, 10)
    )
    const totalImagesInSeries = instances.length
    for (const inst of instances) {
      if (!inst) continue
      const diag = resolveDiagnosticUrl(inst, assetBaseUrl)
      const loadPlan = resolveLoadUrl(inst, initialMode, assetBaseUrl)
      const hasRange =
        inst.dicomFetchType === 'RANGE_ZIP' &&
        !!inst.archiveUrl &&
        inst.byteOffset != null &&
        Number(inst.byteLength) > 0
      const canLoad =
        hasRange ||
        !!diag ||
        !!(loadPlan && loadPlan.url) ||
        !!(loadPlan && loadPlan.dicomFetchType === 'RANGE_ZIP')
      if (!canLoad) continue
      tasks.push({
        series,
        inst,
        absoluteUrl: diag || loadPlan.url || inst.archiveUrl || null,
        totalImagesInSeries,
        seriesIdx,
      })
    }
    seriesIdx += 1
  }

  if (tasks.length === 0) {
    throw new Error(
      assetBaseUrl
        ? 'No hay instancias con url o path resoluble en el manifest'
        : 'No hay instancias con URL en el manifest (faltan url absolutas o base para path)'
    )
  }

  const numSeries = seriesSorted.length

  const firstDisplayableSeriesIdx = (() => {
    for (let i = 0; i < seriesSorted.length; i++) {
      const s = seriesSorted[i]
      const inst =
        (s && Array.isArray(s.instances) && s.instances[0]) || null
      if (!isNonImageSeriesInst(s, inst)) return i
    }
    return 0
  })()

  const resolveActiveSeriesIdxPreferImage = () => {
    let n = resolveActiveSeriesIdx()
    if (!Number.isFinite(n) || n < 0) n = 0
    n = Math.min(numSeries - 1, Math.max(0, Math.floor(n)))
    const s = seriesSorted[n]
    const inst = (s && Array.isArray(s.instances) && s.instances[0]) || null
    if (isNonImageSeriesInst(s, inst)) return firstDisplayableSeriesIdx
    return n
  }

  const seriesSlots = buildSeriesSlots(tasks)

  const seriesInfo = seriesSorted.map((s) => ({
    seriesNumber: parseInt(s.seriesNumber, 10) || 0,
    description: String(s.seriesDescription != null ? s.seriesDescription : '').trim(),
    totalTasks: 0,
  }))
  for (const t of tasks) {
    seriesInfo[t.seriesIdx].totalTasks += 1
  }

  // Offline cache: contadores en memoria (+ snapshot IDB al inicio)
  let offlineFromCacheHits = 0
  let offlineBytesEstimate = 0
  let offlineCompleteAtStart = 0
  let cacheHitsMatchingManifest = 0
  let studyFullyCachedAtStart = false
  let completeSet = new Set()
  let previewLoads = 0
  let previewFallbacks = 0
  let dicomLoads = 0

  const expectedCacheKeys = tasks.map((t) => {
    const sop = sopFromManifestInstance(t.inst)
    return sop || fallbackCacheKey(t.absoluteUrl, t.inst)
  })

  try {
    completeSet = await listCompleteSopsForStudy(studyUid)
    offlineCompleteAtStart = completeSet.size
    if (expectedCacheKeys.length && completeSet.size > 0) {
      let hit = 0
      for (const k of expectedCacheKeys) {
        if (completeSet.has(k)) hit += 1
      }
      cacheHitsMatchingManifest = hit
      // Mantener max (SOP reparseados pueden no matchear keys anticipadas)
      offlineCompleteAtStart = Math.max(hit, Math.min(completeSet.size, tasks.length))
    }
    studyFullyCachedAtStart =
      tasks.length > 0 && cacheHitsMatchingManifest >= tasks.length
    const stats0 = await getCombinedStudyCacheStats(studyUid, expectedCacheKeys)
    offlineBytesEstimate = stats0.bytes || 0
  } catch (e) {
    console.warn('loadStudyFromManifest: offline cache precount', e)
  }

  // Concurrencia conocida antes del scheduler (cuotas activas vs side)
  const zipRangeMode = getR2ZipRangeMode()
  const coalescingOn = zipRangeMode === R2_ZIP_RANGE_MODE.COALESCING
  let parallel = resolveConcurrency(concurrency, tasks.length)
  if (coalescingOn) {
    parallel = getCoalescingFetcherCount(parallel)
  }
  const PROGRESS_THROTTLE_MS = isPhoneLikeDevice()
    ? 550
    : isConstrainedUiDevice()
      ? 400
      : 320

  const hasHeavyCineSeries = seriesSorted.some((s) => {
    const mod = String((s && (s.modality || s.Modality)) || '')
      .trim()
      .toUpperCase()
    return mod === 'XA' || mod === 'XRF' || mod === 'RF'
  })

  const scheduler = createSeriesPriorityScheduler({
    numSeries,
    seriesSlots,
    seriesInfo,
    parallel,
    getActiveSeriesIdx: resolveActiveSeriesIdxPreferImage,
    getPinnedSeriesIdx: resolvePinnedSeriesIdx,
    getScrollFocus: resolveScrollFocus,
    // DIAG + XA: no bajar 4 Part-10 cine en paralelo (congela UI).
    maxSideBudget:
      hasHeavyCineSeries && initialMode !== RENDER_MODE.PREVIEW ? 1 : undefined,
  })
  const { sideBudget, activeCapDefault } = scheduler
  /** Series que ya recibieron su primer bloque (TTFF ≤1MB). */
  const firstBlockDoneBySeries = new Set()

  const progressTracker = createDownloadProgressTracker({
    totalTasks: tasks.length,
    seriesInfo,
    throttleMs: PROGRESS_THROTTLE_MS,
    onProgress: onDownloadProgress,
    loadSource: () => {
      const { loadedOk } = progressTracker.totals
      if (
        studyFullyCachedAtStart ||
        (loadedOk > 0 && offlineFromCacheHits >= loadedOk * 0.8)
      ) {
        return 'local'
      }
      const preferred =
        handlers && handlers.preferredLoadSource
          ? String(handlers.preferredLoadSource)
          : ''
      if (preferred === 'legacy-wado' || preferred === 'r2') return preferred
      return 'r2'
    },
    extraStats: () => {
      const { loadedOk, totalBytes } = progressTracker.totals
      const newNetworkStores = Math.max(0, loadedOk - offlineFromCacheHits)
      const completeApprox = Math.min(
        tasks.length,
        offlineCompleteAtStart + newNetworkStores
      )
      let wq = { queueDepth: 0, bytesQueued: 0, putsCompleted: 0, writesFailed: 0 }
      try {
        wq = getWriteQueueStats()
      } catch (e) {
        /* ignore */
      }
      return {
        studyInstanceUID: studyUid,
        renderMode: resolveMode(),
        previewLoads,
        previewFallbacks,
        dicomLoads,
        offlineCache: {
          completeCount: completeApprox,
          fromCacheHits: offlineFromCacheHits,
          cachedAtStart: cacheHitsMatchingManifest,
          studyFullyCachedAtStart,
          targetCount: tasks.length,
          studyOffline:
            studyFullyCachedAtStart ||
            (completeApprox >= tasks.length && tasks.length > 0),
          bytesCached: offlineBytesEstimate + totalBytes,
          studyInstanceUID: studyUid,
          writeQueueDepth: wq.queueDepth,
          writesPending: wq.queueDepth,
          writesCompleted: wq.putsCompleted,
          writesFailed: wq.writesFailed,
        },
      }
    },
  })

  function maybeEmitProgress(force) {
    progressTracker.emit(!!force)
  }

  const orderedItems = new Array(tasks.length)

  // Primer slot por serie (miniatura explorer) — se conserva `image`
  const firstSlotBySeries = new Map()
  tasks.forEach((t, slot) => {
    if (!firstSlotBySeries.has(t.seriesIdx)) firstSlotBySeries.set(t.seriesIdx, slot)
  })

  /**
   * ¿Retener `image` en el ítem Redux?
   * - 1.ª de cada serie → thumbs Explorer
   * - Ventana ±radius del corte activo → scroll fluido
   * Nunca toda la serie activa (CT 300+ decodes en el store = UI congelada al flush).
   */
  function shouldKeepPixelsInStore(slot, seriesIdx) {
    if (firstSlotBySeries.get(seriesIdx) === slot) return true
    const activeIdx = Math.max(
      0,
      Math.min(numSeries - 1, resolveActiveSeriesIdxPreferImage())
    )
    if (seriesIdx !== activeIdx) return false
    const slots = seriesSlots.get(seriesIdx) || []
    const localIdx = slots.indexOf(slot)
    if (localIdx < 0) return false
    let center = 0
    let radius = 3
    try {
      const focus =
        typeof resolveScrollFocus === 'function' ? resolveScrollFocus() : null
      if (focus && focus.seriesIdx === seriesIdx && focus.sliceIndex != null) {
        center = Math.max(0, Math.floor(Number(focus.sliceIndex) || 0))
      }
      if (focus && focus.radius != null && Number.isFinite(Number(focus.radius))) {
        radius = Math.max(1, Math.floor(Number(focus.radius)))
      }
    } catch (e) {
      /* defaults */
    }
    return Math.abs(localIdx - center) <= radius
  }

  /**
   * Stubs con imageId + metadata para TODO el estudio (sin bytes).
   * Permite scroll/explorer de inmediato; runOne rellena image al descarga.
   */
  function buildSkeletonItem(task, slot) {
    const { series, inst, totalImagesInSeries } = task
    const instanceNumber = parseInt(inst.instanceNumber, 10) || 0
    const seriesNumber = parseInt(series.seriesNumber, 10) || 0
    const mode = resolveMode()
    const plan = resolveLoadUrl(inst, mode, assetBaseUrl)
    const dicomUrl = resolveDiagnosticUrl(inst, assetBaseUrl)
    const planUrl = plan && plan.url ? plan.url : null
    // PREVIEW web-loader usa https crudo; DICOM usa wadouri:
    let imageId = ''
    let renderKind = 'dicom'
    let usedFallback = false
    let previewUrl = null
    if (plan && plan.kind === 'preview' && planUrl) {
      imageId = planUrl
      renderKind = 'preview'
      previewUrl = planUrl
    } else if (planUrl) {
      imageId = /^wadouri:/i.test(planUrl) ? planUrl : `wadouri:${planUrl}`
      renderKind = 'dicom'
      usedFallback = mode === RENDER_MODE.PREVIEW
    } else if (dicomUrl) {
      imageId = `wadouri:${dicomUrl}`
      renderKind = 'dicom'
    }
    // Thumbs XA: conservar preview https aunque el modo sea DIAG (Part-10).
    if (!previewUrl) {
      previewUrl =
        resolvePreviewUrl(inst, assetBaseUrl) || inst.previewUrl || null
    }
    const fetchMetaEarly = extractDicomFetchMeta(inst, series)
    const sopUid =
      sopFromManifestInstance(inst) ||
      (imageId ? fallbackCacheKey(imageId, inst) : null) ||
      (fetchMetaEarly.dicomFetchType === 'RANGE_ZIP' &&
      fetchMetaEarly.archiveUrl &&
      fetchMetaEarly.byteOffset != null
        ? `rz:${fetchMetaEarly.byteOffset}:${fetchMetaEarly.byteLength || 0}`
        : null)
    // RANGE_ZIP DIAG: resolveLoadUrl.url=null → sin imageId real. Placeholder
    // estable para seed Redux/scroll; NO es wadouri (hydrate no debe fetchearlo).
    if (!imageId && sopUid) {
      imageId = `udv-pending:${sopUid}`
      renderKind = 'dicom'
    }
    const fromCache = !!(sopUid && completeSet.has(String(sopUid)))
    const seriesInstanceUID =
      series.seriesInstanceUID ||
      series.SeriesInstanceUID ||
      series.seriesUID ||
      inst.seriesInstanceUID ||
      null

    const fetchMeta = fetchMetaEarly
    const geometry = copyGeometryFromManifest(inst, series)
    const { windowWidth, windowCenter } = windowFromManifestInst(inst)
    const sopClassUID =
      (inst && (inst.sopClassUID || inst.SOPClassUID)) || null
    const isPdfEarly = isPdfDocumentSeriesInst(series, inst)
    // PDF: imageId estable sin wadouri (Explorer / PdfViewport; no prefetch Cornerstone)
    if (isPdfEarly && sopUid) {
      imageId = `udv-pdf:${sopUid}`
      renderKind = 'pdf'
    }
    const stub = {
      imageId,
      instanceNumber,
      name: `${inst.instanceNumber || slot}.dcm`,
      image: null,
      sopInstanceUID: sopUid || null,
      sopClassUID,
      seriesInstanceUID: seriesInstanceUID || null,
      dicomUrl: dicomUrl || null,
      previewUrl: previewUrl || inst.previewUrl || null,
      windowWidth,
      windowCenter,
      ...fetchMeta,
      ...geometry,
      renderKind,
      usedFallback,
      skeleton: true,
      patient: { patientName, patientId },
      study: {
        studyId: studyUid,
        studyDate,
        studyTime,
        studyDateTime: formatStudyDateTime(studyDate, studyTime),
        studyDescription,
      },
      series: {
        seriesDate: series.seriesDate,
        seriesTime: series.seriesTime,
        seriesDescription: series.seriesDescription || '',
        seriesNumber,
        seriesInstanceUID: seriesInstanceUID || null,
        echoNumber: parseInt(series.echoNumber, 10) || 0,
        totalImages: totalImagesInSeries,
        modality: series.modality || series.Modality || inst.modality || inst.Modality || '',
        sopClassUID,
        role: series.role || undefined,
        storage: series.storage || undefined,
        imageType: series.imageType || inst.imageType || undefined,
        isScout: isScoutSeries({
          imageType: series.imageType || inst.imageType,
          modality: series.modality || series.Modality || inst.modality || inst.Modality || '',
          seriesDescription: series.seriesDescription || '',
          instanceCount: totalImagesInSeries,
        }),
      },
      fromCache,
    }
    stampNonImageOnFileItem(stub)
    if (stub.isPdfDocument || stub.renderKind === 'pdf' || isPdfEarly) {
      stub.renderKind = 'pdf'
      stub.isPdfDocument = true
    } else if (stub.nonImage) {
      stub.renderKind = 'nonimage'
    }
    return stub
  }

  let stripCounter = 0

  // Seed inmediato: Redux tiene N frames scrolleables antes de bajar un byte.
  // RANGE_ZIP usa udv-pending:/sop; no exigir URL https real.
  for (let slot = 0; slot < tasks.length; slot++) {
    const stub = buildSkeletonItem(tasks[slot], slot)
    if (stub.imageId || stub.sopInstanceUID || stub.skeleton) {
      orderedItems[slot] = stub
    }
  }

  // IPP/IOP: inst. 1 suele venir sin tags en manifest; interpolar antes de PREVIEW ref-lines
  const seriesFileGroups = new Map()
  for (let slot = 0; slot < tasks.length; slot++) {
    const item = orderedItems[slot]
    if (!item) continue
    const si = tasks[slot].seriesIdx
    if (!seriesFileGroups.has(si)) seriesFileGroups.set(si, [])
    seriesFileGroups.get(si).push(item)
  }
  seriesFileGroups.forEach((group) => {
    try {
      spreadStackGeometryFromFiles(group)
    } catch (eSpread) {
      /* ignore */
    }
  })

  /**
   * Reuso en hot-update preview: ítems ya cargados en memoria (misma sesión).
   * Evita resetear el contador a 0 y re-descargar WebP ya en Redux/Cornerstone.
   */
  const reuseBySop = new Map()
  const reuseList = Array.isArray(handlers && handlers.reuseItems)
    ? handlers.reuseItems
    : []
  for (let ri = 0; ri < reuseList.length; ri++) {
    const it = reuseList[ri]
    if (!it || typeof it !== 'object') continue
    const sop = it.sopInstanceUID != null ? String(it.sopInstanceUID) : ''
    if (!sop) continue
    const usable =
      !it.error &&
      !!it.imageId &&
      (it.image || it.fromCache || it.skeleton === false)
    if (!usable) continue
    if (!reuseBySop.has(sop)) reuseBySop.set(sop, it)
  }

  let reusedSlots = 0
  if (reuseBySop.size > 0) {
    for (let slot = 0; slot < tasks.length; slot++) {
      const task = tasks[slot]
      if (!task) continue
      const sop =
        sopFromManifestInstance(task.inst) ||
        fallbackCacheKey(task.absoluteUrl, task.inst)
      if (!sop || !reuseBySop.has(String(sop))) continue
      const prev = reuseBySop.get(String(sop))
      const stub = orderedItems[slot] || buildSkeletonItem(task, slot)
      // Conservar pixels/imageId previos; actualizar totales de serie del manifest nuevo
      orderedItems[slot] = {
        ...stub,
        ...prev,
        imageId: prev.imageId || stub.imageId,
        image: prev.image != null ? prev.image : stub.image,
        previewUrl: prev.previewUrl || stub.previewUrl,
        fromCache: true,
        skeleton: false,
        series: {
          ...(stub.series || {}),
          ...(prev.series || {}),
          totalImages: task.totalImagesInSeries,
          seriesNumber:
            (prev.series && prev.series.seriesNumber) ||
            (stub.series && stub.series.seriesNumber) ||
            0,
        },
      }
      reusedSlots += 1
      offlineFromCacheHits += 1
      scheduler.finishSlot(slot)
      progressTracker.recordTaskDone({
        seriesIdx: task.seriesIdx,
        ok: true,
        bytes: 0,
      })
    }
  }
  if (reusedSlots > 0) {
    try {
      // eslint-disable-next-line no-console
      console.info(
        '[UDV] preview-grow reuse slots=',
        reusedSlots,
        '/',
        tasks.length
      )
    } catch (e) {
      /* ignore */
    }
  }

  function stripNonActiveImages() {
    for (let i = 0; i < orderedItems.length; i++) {
      const it = orderedItems[i]
      if (!it || !it.image) continue
      const t = tasks[i]
      if (!t) continue
      // Solo 1.ª de serie (thumbs) + ventana de scroll de la activa.
      // Antes DIAG guardaba TODA la serie activa → freeze al terminar CT 200–500 cortes.
      if (shouldKeepPixelsInStore(i, t.seriesIdx)) continue
      // Suelto `image` del store para RAM Redux; NO purgar cornerstone.imageCache
      // (reuso al volver PREVIEW↔DIAG y al scrollear de nuevo).
      it.image = null
    }
  }

  const applyReduxFlush = () => {
    stripNonActiveImages()
    const itemsSoFar = orderedItems.filter(Boolean)
    if (itemsSoFar.length > 0) {
      uniquifySeriesNumbers(itemsSoFar)
      setFilesStore(itemsSoFar)
      onExplorerUpdate(buildExplorerData(itemsSoFar))
      isOpenStore({ index: activeDcmIndex, value: true })
    }
  }

  let reduxFlushDeferred = false
  let unsubInteraction = null
  let idleFlushHandle = null

  const flushReduxInner = () => {
    // Mid-gesto: no clonar 287 items + re-render App/Explorer (quema CPU).
    // Viewport recibe pixels por onInstanceReady; store se pone al día en idle.
    if (shouldDeferHeavyUiFlush()) {
      reduxFlushDeferred = true
      if (idleFlushHandle == null) {
        idleFlushHandle = scheduleWhenIdle(() => {
          idleFlushHandle = null
          if (reduxFlushDeferred && !shouldDeferHeavyUiFlush()) {
            reduxFlushDeferred = false
            try {
              applyReduxFlush()
            } catch (e) {
              /* ignore */
            }
          }
        }, { timeout: 1200 })
      }
      return
    }
    reduxFlushDeferred = false
    applyReduxFlush()
  }

  try {
    unsubInteraction = subscribeInteraction((state) => {
      if (state && !state.isInteracting && reduxFlushDeferred) {
        reduxFlushDeferred = false
        try {
          applyReduxFlush()
        } catch (e) {
          /* ignore */
        }
      }
    })
  } catch (eSub) {
    unsubInteraction = null
  }

  // Throttle base; mid-gesto se saltea por shouldDeferHeavyUiFlush
  const flushReduxThrottled = throttle(
    flushReduxInner,
    getManifestReduxFlushMs(tasks.length),
    { leading: true, trailing: true }
  )

  // Publicar stack esqueleto YA (scroll sin esperar la descarga completa)
  try {
    flushReduxInner()
  } catch (e) {
    console.warn('loadStudyFromManifest: skeleton flush', e)
  }
  if (typeof (handlers && handlers.onStackSeeded) === 'function') {
    try {
      handlers.onStackSeeded({
        totalTasks: tasks.length,
        renderMode: resolveMode(),
        loadSource: studyFullyCachedAtStart ? 'local' : 'r2',
        studyFullyCachedAtStart,
      })
    } catch (e) {
      console.warn('loadStudyFromManifest: onStackSeeded', e)
    }
  }
  // Catálogo temprano: Local Studies ve el estudio mientras descarga (si cache permitido)
  if (offlineCacheAllowed !== false && !skipIdbWrite) {
    try {
      const catalogOrigin =
        handlers && handlers.catalogOrigin && typeof handlers.catalogOrigin === 'object'
          ? handlers.catalogOrigin
          : null
      void syncStudyCatalogFromManifest(manifest, {
        assetBaseUrl,
        expectedSopList: expectedCacheKeys,
        source: handlers && handlers.catalogSource,
        edgeId: catalogOrigin && catalogOrigin.edgeId,
        clinicName: catalogOrigin && catalogOrigin.clinicName,
      }).catch((eEarly) => {
        console.warn('[loadStudyFromManifest] early catalog sync', eEarly)
      })
    } catch (eCat) {
      /* ignore */
    }
  }
  // Chip Local/R2 en cuanto termina el precount IDB (antes del primer frame)
  maybeEmitProgress(true)

  function flushProgress() {
    // Stats sin clonar lista de files completa más de lo necesario:
    // progress y redux van en el mismo throttle de files; progress más frecuente en maybeEmit
    flushReduxThrottled()
    maybeEmitProgress(false)
  }

  function scheduleProgress() {
    flushProgress()
  }

  function isRangeZipTask(task) {
    const inst = task && task.inst
    if (!inst) return false
    const series = task.series || null
    if (isReportLooseSeriesInst(series, inst)) return false
    if (inst.dicomFetchType === DICOM_FETCH_TYPE.DIRECT_URL) return false
    return (
      (inst.dicomFetchType === DICOM_FETCH_TYPE.RANGE_ZIP ||
        (inst.archiveUrl && inst.byteOffset != null && Number(inst.byteLength) > 0)) &&
      !!inst.archiveUrl &&
      Number.isFinite(Number(inst.byteOffset)) &&
      Number(inst.byteLength) > 0
    )
  }

  function recordSlotProgress(slot, seriesIdx) {
    const row = orderedItems[slot]
    const ok = !!(row && (row.image || row.imageId) && !row.error)
    const bytes = ok ? estimateLoadedBytes(row && row.image) : 0
    progressTracker.recordTaskDone({ seriesIdx, ok, bytes })
    scheduleProgress()
  }

  /**
   * Persiste resultado OK de una instancia (path compartido runOne / coalescing).
   */
  function persistLoadedImage(task, slot, loaded) {
    const { series, inst, totalImagesInSeries, seriesIdx } = task
    const instanceNumber = parseInt(inst.instanceNumber, 10) || 0
    const seriesNumber = parseInt(series.seriesNumber, 10) || 0
    const image = loaded.image
    let imageId = loaded.imageId || ''
    let sopUid = loaded.sopInstanceUID || sopFromManifestInstance(inst)
    const fromCache = !!loaded.fromCache
    const isPdf =
      !!loaded.isPdfDocument ||
      loaded.renderKind === 'pdf' ||
      loaded.loadKind === 'pdf' ||
      isPdfDocumentSeriesInst(series, inst)
    const renderKind = isPdf
      ? 'pdf'
      : loaded.renderKind || loaded.loadKind || 'dicom'
    const usedFallback = !!loaded.usedFallback
    const nonImage =
      isPdf ||
      !!loaded.nonImage ||
      renderKind === 'nonimage' ||
      isNonImageSeriesInst(series, inst)

    if (fromCache) offlineFromCacheHits += 1
    if (renderKind === 'preview') {
      previewLoads += 1
    } else {
      dicomLoads += 1
      if (usedFallback) previewFallbacks += 1
    }

    const mode = resolveMode()
    // No retener toda la serie activa en Redux (CT/MR multi-SOP congela al flush final).
    // Thumbs = 1.ª; viewport = onInstanceReady + imageCache + ventana de scroll.
    const keepPixels = !nonImage && shouldKeepPixelsInStore(slot, seriesIdx)

    let seriesInstanceUID =
      series.seriesInstanceUID ||
      series.SeriesInstanceUID ||
      series.seriesUID ||
      inst.seriesInstanceUID ||
      null
    if (!seriesInstanceUID && image && image.data) {
      try {
        seriesInstanceUID = image.data.string('x0020000e') || null
      } catch (e) {
        /* ignore */
      }
    }

    const dicomUrl = resolveDiagnosticUrl(inst, assetBaseUrl)
    const plan = resolveLoadUrl(inst, mode, assetBaseUrl)
    const fetchMeta = extractDicomFetchMeta(inst, series)
    const geometry = {
      ...copyGeometryFromManifest(inst, series),
      ...(orderedItems[slot] || {}),
    }
    const { windowWidth, windowCenter } = windowFromManifestInst(inst)
    const sopClassUID =
      (inst && (inst.sopClassUID || inst.SOPClassUID)) || null

    orderedItems[slot] = {
      imageId,
      instanceNumber,
      name: `${inst.instanceNumber || slot}.dcm`,
      image: keepPixels ? image : null,
      data: loaded.data || null,
      sopInstanceUID: sopUid || null,
      sopClassUID,
      seriesInstanceUID: seriesInstanceUID || null,
      dicomUrl: dicomUrl || null,
      previewUrl:
        (plan.kind === 'preview' ? plan.url : null) ||
        resolvePreviewUrl(inst, assetBaseUrl) ||
        inst.previewUrl ||
        null,
      windowWidth,
      windowCenter,
      ...fetchMeta,
      imagePositionPatient: geometry.imagePositionPatient,
      imageOrientationPatient: geometry.imageOrientationPatient,
      sliceThickness: geometry.sliceThickness,
      sliceLocation: geometry.sliceLocation,
      frameOfReferenceUID: geometry.frameOfReferenceUID,
      pixelSpacing: geometry.pixelSpacing,
      rows: geometry.rows,
      columns: geometry.columns,
      renderKind: isPdf ? 'pdf' : nonImage ? 'nonimage' : renderKind,
      usedFallback,
      nonImage,
      isPdfDocument: isPdf,
      skeleton: false,
      patient: { patientName, patientId },
      study: {
        studyId: studyUid,
        studyDate,
        studyTime,
        studyDateTime: formatStudyDateTime(studyDate, studyTime),
        studyDescription,
      },
      series: {
        seriesDate: series.seriesDate,
        seriesTime: series.seriesTime,
        seriesDescription: series.seriesDescription || '',
        seriesNumber,
        seriesInstanceUID: seriesInstanceUID || null,
        echoNumber: parseInt(series.echoNumber, 10) || 0,
        totalImages: totalImagesInSeries,
        modality: series.modality || series.Modality || inst.modality || inst.Modality || '',
        sopClassUID,
        isNonImage: nonImage,
      },
      fromCache,
    }
    stampSliceGeometry(orderedItems[slot], image)
    stampIsScoutOnFileItem(orderedItems[slot])
    stampIsRefOnFileItem(orderedItems[slot])
    stampNonImageOnFileItem(orderedItems[slot])

    // Viewport mid-scroll: publicar pixels sin esperar Redux (RANGE_ZIP sin imageId).
    // PR/non-image: no notificar paint (no hay image).
    if (!nonImage && image) {
      notifyInstanceReady(
        { ...orderedItems[slot], image },
        { seriesIdx, slot, instanceNumber }
      )
    }

    stripCounter += 1
    if (stripCounter % 25 === 0) {
      stripNonActiveImages()
    }
    recordSlotProgress(slot, seriesIdx)
  }

  function persistLoadError(task, slot, err, imageIdHint) {
    const { series, inst, totalImagesInSeries, seriesIdx } = task
    const instanceNumber = parseInt(inst.instanceNumber, 10) || 0
    const seriesNumber = parseInt(series.seriesNumber, 10) || 0
    const sopUid = sopFromManifestInstance(inst)
    const fetchMetaErr = extractDicomFetchMeta(inst, series)
    const geometryErr = copyGeometryFromManifest(inst, series)
    const { windowWidth, windowCenter } = windowFromManifestInst(inst)
    const sopClassUIDErr =
      (inst && (inst.sopClassUID || inst.SOPClassUID)) || null
    const isPdfErr = isPdfDocumentSeriesInst(series, inst)
    orderedItems[slot] = {
      imageId: imageIdHint || (isPdfErr && sopUid ? `udv-pdf:${sopUid}` : ''),
      instanceNumber,
      name: `${inst.instanceNumber || slot}.dcm`,
      image: null,
      sopInstanceUID: sopUid || null,
      sopClassUID: sopClassUIDErr,
      seriesInstanceUID:
        series.seriesInstanceUID ||
        series.SeriesInstanceUID ||
        series.seriesUID ||
        null,
      dicomUrl: resolveDiagnosticUrl(inst, assetBaseUrl) || null,
      previewUrl: inst.previewUrl || null,
      windowWidth,
      windowCenter,
      ...fetchMetaErr,
      ...geometryErr,
      error: (err && err.message) || String(err),
      renderKind: isPdfErr ? 'pdf' : 'dicom',
      isPdfDocument: isPdfErr,
      nonImage: isPdfErr || isNonImageSeriesInst(series, inst),
      usedFallback: false,
      skeleton: true,
      patient: { patientName, patientId },
      study: {
        studyId: studyUid,
        studyDate,
        studyTime,
        studyDateTime: formatStudyDateTime(studyDate, studyTime),
        studyDescription,
      },
      series: {
        seriesDate: series.seriesDate,
        seriesTime: series.seriesTime,
        seriesDescription: series.seriesDescription || '',
        seriesNumber,
        seriesInstanceUID:
          series.seriesInstanceUID ||
          series.SeriesInstanceUID ||
          series.seriesUID ||
          null,
        echoNumber: parseInt(series.echoNumber, 10) || 0,
        totalImages: totalImagesInSeries,
        modality: series.modality || series.Modality || inst.modality || inst.Modality || '',
        sopClassUID: sopClassUIDErr,
        role: series.role || undefined,
        storage: series.storage || undefined,
      },
    }
    recordSlotProgress(slot, seriesIdx)
  }

  /**
   * Coalescing: solo el seed queda inFlight durante el fetch.
   * Tras el buffer, claim+decode vecinos aún abiertos (máx 4).
   * Timeout 45s; si falla el fetch → false (legacy runOne del seed).
   * @returns {Promise<boolean>}
   */
  async function tryRunCoalescedBlock(seedSlot) {
    if (!coalescingOn) return false
    const seedTask = tasks[seedSlot]
    if (!isRangeZipTask(seedTask)) return false

    const mode = resolveMode()
    if (mode === RENDER_MODE.PREVIEW) return false

    const activeIdx = Math.max(0, Math.min(numSeries - 1, resolveActiveSeriesIdx()))
    if (seedTask.seriesIdx !== activeIdx) return false

    const inst = seedTask.inst
    const seedCand = {
      slot: seedSlot,
      byteOffset: Number(inst.byteOffset),
      byteLength: Number(inst.byteLength),
      seriesIdx: seedTask.seriesIdx,
      archiveUrl: String(inst.archiveUrl),
    }

    const openCands = [seedCand]
    for (let s = 0; s < tasks.length; s++) {
      if (s === seedSlot) continue
      if (!scheduler.isSlotOpen(s)) continue
      const t = tasks[s]
      if (!isRangeZipTask(t)) continue
      if (t.seriesIdx !== seedTask.seriesIdx) continue
      if (String(t.inst.archiveUrl) !== seedCand.archiveUrl) continue
      openCands.push({
        slot: s,
        byteOffset: Number(t.inst.byteOffset),
        byteLength: Number(t.inst.byteLength),
        seriesIdx: t.seriesIdx,
        archiveUrl: String(t.inst.archiveUrl),
      })
    }

    const isFirstBlock = !firstBlockDoneBySeries.has(seedTask.seriesIdx)
    const maxBlockBytes = isFirstBlock
      ? FIRST_BLOCK_MAX_BYTES
      : MAX_COALESCED_BLOCK_SIZE_BYTES

    const block = buildCoalescedBlock(seedCand, openCands, {
      maxBlockBytes,
      maxMembers: Math.min(4, MAX_COALESCED_MEMBERS),
      preferForward: true,
    })
    if (!block || !block.members || block.members.length < 2) return false

    firstBlockDoneBySeries.add(seedTask.seriesIdx)

    const FETCH_TIMEOUT_MS = 45000
    let blockBuffer
    try {
      assertNotCancelled()
      const ac =
        typeof AbortController !== 'undefined' ? new AbortController() : null
      const timer =
        ac &&
        setTimeout(() => {
          try {
            ac.abort()
          } catch (e) {
            /* ignore */
          }
        }, FETCH_TIMEOUT_MS)
      try {
        blockBuffer = await fetchZipRangeBlock({
          archiveUrl: block.archiveUrl,
          rangeStart: block.rangeStart,
          rangeEnd: block.rangeEnd,
          signal: ac ? ac.signal : undefined,
        })
      } finally {
        if (timer) clearTimeout(timer)
      }
      assertNotCancelled()
    } catch (e) {
      if (e && e.name === 'AbortError' && cancelled()) throw e
      console.warn(
        'loadStudyFromManifest: coalesced fetch failed, legacy seed',
        e && e.message
      )
      return false
    }

    // Seed ya inFlight. Vecinos: claim solo si siguen abiertos (post-fetch).
    for (let i = 0; i < block.members.length; i++) {
      const m = block.members[i]
      const task = tasks[m.slot]
      const isSeed = m.slot === seedSlot

      if (!isSeed) {
        if (!scheduler.isSlotOpen(m.slot)) continue
        if (!scheduler.tryClaimSlot(m.slot)) continue
      }

      try {
        assertNotCancelled()
        if (
          !isSeed &&
          Math.max(0, Math.min(numSeries - 1, resolveActiveSeriesIdx())) !==
            seedTask.seriesIdx
        ) {
          scheduler.releaseInFlight(m.slot)
          break
        }

        const part = slicePart10FromBlock(blockBuffer, m)
        if (!part || part.byteLength === 0) {
          throw new Error('slice Part-10 vacío')
        }
        const tag = `${block.archiveUrl}#bytes=${m.byteOffset}-${m.byteOffset + m.byteLength - 1}`
        const loaded = await decodeAndCachePart10Buffer({
          buffer: part,
          inst: task.inst,
          series: task.series,
          studyInstanceUID: studyUid,
          absoluteUrlTag: tag,
          skipIdbWrite,
          fromCache: false,
        })
        persistLoadedImage(task, m.slot, loaded)
        scheduler.finishSlot(m.slot)
      } catch (memberErr) {
        if (memberErr && memberErr.name === 'AbortError') {
          if (!isSeed) {
            try {
              scheduler.releaseInFlight(m.slot)
            } catch (eRel) {
              /* ignore */
            }
          }
          throw memberErr
        }
        if (isSeed) {
          // Worker hará runOne(seed)
          return false
        }
        try {
          await runOne(task, m.slot)
        } catch (e2) {
          if (e2 && e2.name === 'AbortError') {
            try {
              scheduler.releaseInFlight(m.slot)
            } catch (eRel) {
              /* ignore */
            }
            throw e2
          }
          persistLoadError(task, m.slot, memberErr || e2)
        }
        scheduler.finishSlot(m.slot)
      }

      await new Promise((r) => setTimeout(r, 0))
    }

    // Seed aún inFlight → el worker debe hacer runOne
    if (scheduler.inFlight.has(seedSlot)) {
      return false
    }
    return true
  }

  async function runOne(task, slot) {
    assertNotCancelled()
    const { series, inst, absoluteUrl, seriesIdx } = task
    const mode = resolveMode()

    try {
      let loaded
      try {
        loaded = await loadInstanceForMode({
          inst,
          series,
          assetBaseUrl,
          studyInstanceUID: studyUid,
          renderMode: mode,
          absoluteUrl,
          skipIdbWrite,
          skipIdbRead,
        })
      } catch (cacheErr) {
        assertNotCancelled()
        // PR/GSPS: no fallback wadouri (Cornerstone exigiría Pixel Data).
        if (isNonImageSeriesInst(series, inst)) {
          throw cacheErr
        }
        console.warn('loadStudyFromManifest: mode path failed, legacy URL', cacheErr)
        const fallbackUrl = absoluteUrl || resolveDiagnosticUrl(inst, assetBaseUrl)
        if (!fallbackUrl) throw cacheErr
        const direct = await loadDicomInstanceDirectUrl(fallbackUrl)
        loaded = {
          image: direct.image,
          imageId: direct.imageId,
          sopInstanceUID: direct.sopInstanceUID || sopFromManifestInstance(inst),
          fromCache: false,
          renderKind: 'dicom',
          loadKind: 'dicom',
          usedFallback: mode === RENDER_MODE.PREVIEW,
        }
      }

      assertNotCancelled()
      persistLoadedImage(task, slot, loaded)
    } catch (e) {
      if (e && e.name === 'AbortError') throw e
      persistLoadError(task, slot, e)
    }
  }

  async function worker() {
    while (!scheduler.isDone() && scheduler.completed.size < tasks.length) {
      if (cancelled()) return

      // Mid-scroll: menos decode/fetch en vuelo → CPU para paint
      const cap = getAdjustedConcurrency(parallel)
      if (scheduler.inFlight && scheduler.inFlight.size >= cap) {
        await new Promise((r) => setTimeout(r, 16))
        continue
      }

      const slot = scheduler.pickNextSlot()
      if (slot === null) {
        if (scheduler.completed.size >= tasks.length) return
        // Evitar spin eterno si quedó algún inFlight huérfano
        if (scheduler.inFlight && scheduler.inFlight.size > 0) {
          await new Promise((r) => setTimeout(r, 8))
        } else {
          await new Promise((r) => setTimeout(r, 2))
        }
        continue
      }

      try {
        assertNotCancelled()
        let handled = false
        if (coalescingOn) {
          try {
            const didBlock = await tryRunCoalescedBlock(slot)
            // Bloque completo (seed ya finishSlot adentro)
            if (didBlock && !scheduler.inFlight.has(slot)) {
              handled = true
            }
          } catch (eCo) {
            if (eCo && eCo.name === 'AbortError' && cancelled()) throw eCo
            // Fallo coalescing → legacy abajo
          }
        }
        // Path 0.20.60: 1 instancia = 1 fetch/decode
        if (!handled && scheduler.inFlight.has(slot)) {
          await runOne(tasks[slot], slot)
        }
      } catch (e) {
        if (e && e.name === 'AbortError' && cancelled()) {
          try {
            if (scheduler.inFlight.has(slot)) scheduler.releaseInFlight(slot)
          } catch (eRel) {
            /* ignore */
          }
          return
        }
        // runOne / coalescing ya registran fallos de instancia; no tumbar el pool
      } finally {
        // Crítico (0.20.60): nunca dejar el slot en inFlight
        try {
          if (scheduler.inFlight.has(slot)) {
            scheduler.finishSlot(slot)
          }
        } catch (eFin) {
          /* ignore */
        }
      }
    }
  }

  try {
    const profile = describeDownloadProfile()
    // eslint-disable-next-line no-console
    console.info(
      '[UDV] manifest load parallel=',
      parallel,
      'sideBudget=',
      sideBudget,
      'activeCap=',
      activeCapDefault,
      'zipRangeMode=',
      zipRangeMode,
      'tasks=',
      tasks.length,
      'offlineAtStart=',
      offlineCompleteAtStart,
      profile
    )
  } catch (e) {
    /* ignore */
  }
  maybeEmitProgress(true)
  try {
    await Promise.all(Array.from({ length: parallel }, () => worker()))
  } finally {
    try {
      if (typeof unsubInteraction === 'function') unsubInteraction()
    } catch (eUn) {
      /* ignore */
    }
    unsubInteraction = null
    idleFlushHandle = null
  }

  if (cancelled()) {
    throw createStudyLoadAbortError()
  }

  // Refinar contadores offline reales desde IDB al finalizar
  try {
    // Dejar flushear puts async de schedulePutComplete
    await new Promise((r) => setTimeout(r, 400))
    if (cancelled()) throw createStudyLoadAbortError()
    const finalStats = await getCombinedStudyCacheStats(studyUid, expectedCacheKeys)
    offlineBytesEstimate = finalStats.bytes || 0
    if (finalStats.cachedComplete > 0) {
      offlineCompleteAtStart = finalStats.cachedComplete
    } else if (progressTracker.totals.loadedOk > 0) {
      // Puts pueden retrasarse: usar loadedOk como disponible offline optimista
      offlineCompleteAtStart = progressTracker.totals.loadedOk
    }
  } catch (e) {
    /* ignore */
  }

  flushReduxThrottled.flush()
  try {
    flushReduxInner()
  } catch (eForce) {
    /* ignore */
  }
  maybeEmitProgress(true)

  if (cancelled()) {
    throw createStudyLoadAbortError()
  }

  const items = orderedItems.filter(Boolean)
  const okItems = items.filter((it) => it.imageId || it.image)
  if (okItems.length === 0) {
    throw new Error('No se pudo cargar ninguna imagen del manifest')
  }

  uniquifySeriesNumbers(items)
  setFilesStore([...items])
  onExplorerUpdate(buildExplorerData(items))
  isOpenStore({ index: activeDcmIndex, value: true })

  const catalogSyncAllowed =
    offlineCacheAllowed !== false && !skipIdbWrite
  if (catalogSyncAllowed) {
    try {
      const catalogOrigin =
        handlers && handlers.catalogOrigin && typeof handlers.catalogOrigin === 'object'
          ? handlers.catalogOrigin
          : null
      await syncStudyCatalogFromManifest(manifest, {
        assetBaseUrl,
        expectedSopList: expectedCacheKeys,
        source: handlers && handlers.catalogSource,
        edgeId: catalogOrigin && catalogOrigin.edgeId,
        clinicName: catalogOrigin && catalogOrigin.clinicName,
      })
    } catch (e) {
      console.warn('[loadStudyFromManifest] syncStudyCatalogFromManifest', e)
    }
  }

  const mediaCaps = inspectManifestMedia(manifest, assetBaseUrl)

  return {
    manifest,
    instanceCount: items.length,
    loadedCount: okItems.length,
    renderMode: resolveMode(),
    mediaCaps,
    /** Stack final (ligero: images pueden ser null tras strip) */
    items: items.map((it) => ({
      ...it,
      image: it.image || null,
    })),
  }
}

/** Deep-link ?manifestUrl= */
export async function loadStudyFromManifest(manifestUrl, handlers) {
  const signal = handlers && handlers.signal
  const isCancelled = handlers && handlers.isCancelled
  throwIfStudyLoadCancelled(signal, isCancelled)
  const res = await fetch(manifestUrl, {
    credentials: 'omit',
    signal: signal || undefined,
  })
  if (!res.ok) {
    throw new Error(`No se pudo obtener el manifest (${res.status})`)
  }
  const manifest = await res.json()
  throwIfStudyLoadCancelled(signal, isCancelled)
  const baseFromUrl = originBaseFromUrl(manifestUrl)
  return loadStudyFromManifestObject(manifest, {
    ...(handlers || {}),
    manifestUrl,
    assetBaseUrl: (handlers && handlers.assetBaseUrl) || baseFromUrl,
  })
}
